"use client"

import type React from "react"
import { useState } from "react"
import { Button } from "@/form/components/ui/button"
import { Card } from "@/form/components/ui/card"
import { Input } from "@/form/components/ui/input"
import { Label } from "@/form/components/ui/label"
import { Textarea } from "@/form/components/ui/textarea"
import { Checkbox } from "@/form/components/ui/checkbox"
import { Link } from "react-router-dom"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/form/components/ui/select"
import { ChevronLeft, ChevronRight } from "lucide-react"
import { createClient } from "@/form/lib/supabase/client"
import circlecheck from '../../assets/icons/circle-check.svg';

interface FormData {
  // Step 1
  companySize: string
  industry: string
  aiAreas: string[]

  // Step 2
  currentChallenge: string
  mainGoal: string
  specialMessage: string
  budget: string

  // Step 3
  interestedSolutions: string[]

  // Step 4
  firstName: string
  lastName: string
  email: string
}

const industries = [
  "Automotive",
  "Banking & Finance",
  "Biotechnologie",
  "Chemie",
  "E-Commerce",
  "Einzelhandel",
  "Energie",
  "Gesundheitswesen",
  "Immobilien",
  "IT & Software",
  "Konsumgüter",
  "Logistik & Transport",
  "Maschinenbau",
  "Medien & Marketing",
  "Pharma",
  "Produktion",
  "Rechtsberatung",
  "Telekommunikation",
  "Tourismus",
  "Unternehmensberatung",
  "Versicherungen",
  "Sonstiges",
]

const aiAreas = ["Sales", "Marketing", "Operations", "HR", "Support", "Finance", "Analytics"]
const challenges = ["Leadgenerierung", "Automatisierung", "Skalierung", "Datenanalyse", "Mitarbeiterentlastung"]
const goals = ["Kosten reduzieren", "Umsatz steigern", "Effizienz erhöhen", "Kundenerlebnis verbessern"]
const budgetRanges = ["0-5K €", "5K-15K €", "15K-30K €", "30K+ €"]

const solutions = [
  "KI-Beratung & Transformation",
  "KI-Sales-Pipeline",
  "KI-Leadgenerierung",
  "KI-Cold-Email-Systeme",
  "KI-Content-Systeme",
  "KI-E-Mail-Automatisierung",
  "KI-Rechnungsverarbeitung",
  "KI-Dokumentenverarbeitung",
  "Agentic Workflows",
  "KI-Company-Brain",
  "KI-Business-Analytics",
  "KI-Predictive-Insights",
  "KI-Automated-SEO & GEO",
  "KI-Support-Agent",
  "KI-Support-Automatisierung",
  "KI-HR-Intake-Systeme",
]

export default function AIPricingForm() {
  const [currentStep, setCurrentStep] = useState(1)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const [formData, setFormData] = useState<FormData>({
    companySize: "",
    industry: "",
    aiAreas: [],
    currentChallenge: "",
    mainGoal: "",
    specialMessage: "",
    budget: "",
    interestedSolutions: [],
    firstName: "",
    lastName: "",
    email: "",
  })

  const handleCheckboxChange = (value: string, field: "aiAreas" | "interestedSolutions") => {
    setFormData((prev) => ({
      ...prev,
      [field]: prev[field].includes(value) ? prev[field].filter((item) => item !== value) : [...prev[field], value],
    }))
  }

  const isStepValid = (step: number): boolean => {
    switch (step) {
      case 1:
        return Boolean(formData.companySize) && Boolean(formData.industry) && formData.aiAreas.length > 0
      case 2:
        return Boolean(formData.currentChallenge) && Boolean(formData.mainGoal) && Boolean(formData.budget)
      case 3:
        return formData.interestedSolutions.length > 0
      case 4:
        return Boolean(formData.firstName) && Boolean(formData.lastName) && Boolean(formData.email)
      default:
        return false
    }
  }

  const nextStep = () => {
    if (currentStep < 4 && isStepValid(currentStep)) {
      setCurrentStep(currentStep + 1)
    }
  }

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!isStepValid(4)) return

    setIsSubmitting(true)

    try {
      // Format the message with all form data
      const message = `
        Unternehmensgröße: ${formData.companySize}
        Branche: ${formData.industry}
        KI-Bereiche: ${formData.aiAreas.join(', ')}
        Aktuelle Herausforderung: ${formData.currentChallenge}
        Hauptziel: ${formData.mainGoal}
        Budget: ${formData.budget}
        Interessante Lösungen: ${formData.interestedSolutions.join(', ')}
        Sonderwünsche: ${formData.specialMessage || 'Keine'}
      `;

      // Send to our API endpoint on the standalone server
      const apiUrl = process.env.NODE_ENV === 'development' 
        ? 'http://localhost:3001/api/send-inquiry-email'
        : '/api/send-inquiry-email';
      
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          company_size: formData.companySize,
          industry: formData.industry,
          ai_areas: formData.aiAreas,
          current_challenge: formData.currentChallenge,
          main_goal: formData.mainGoal,
          special_message: formData.specialMessage,
          budget: formData.budget,
          interested_solutions: formData.interestedSolutions,
          message: message
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to send message');
      }

      // If you still want to save to Supabase, uncomment this section
      /*
      const supabase = createClient();
      const { error } = await supabase.from("pricing_inquiries").insert({
        company_size: formData.companySize,
        industry: formData.industry,
        ai_areas: formData.aiAreas,
        current_challenge: formData.currentChallenge,
        main_goal: formData.mainGoal,
        special_message: formData.specialMessage || null,
        budget: formData.budget,
        interested_solutions: formData.interestedSolutions,
        first_name: formData.firstName,
        last_name: formData.lastName,
        email: formData.email,
      });
      if (error) throw error;
      */

      setIsSubmitted(true);
    } catch (error: unknown) {
      console.error('Error submitting form:', error);
      const errorMessage = error instanceof Error ? error.message : 'Es gab einen Fehler beim Senden. Bitte versuchen Sie es erneut.';
      alert(errorMessage);
    } finally {
      setIsSubmitting(false);
    }
  }

  if (isSubmitted) {
    return (
      <Card
        className="w-full max-w-[500px] h-auto min-h-[600px] flex items-center justify-center p-6 border-none shadow-lg sm:w-[90vw]"
        style={{ backgroundColor: "#0f0f11" }}
      >
        <div className="text-center space-y-4">
          <div className="w-12 h-12 flex items-center justify-center mx-auto">
            <img 
              src={circlecheck} 
              alt="Checkmark" 
              className="w-12 h-12" 
            />
          </div>
          <h2 className="text-2xl font-semibold text-white">Erfolgreich gesendet!</h2>
          <p className="text-gray-300">
            Vielen Dank für Ihre Anfrage. Wir senden Ihnen schnellstmöglich eine individuelle Kostenschätzung zu.
          </p>
          <p className="text-sm text-[#787c87] max-w-[500px] mx-auto">
            Zusätzlich erhalten Sie eine Bestätigungs E-Mail. <br/>Mit einem Klick auf den Link stellen Sie sicher, dass unsere Nachrichten nicht im Spam-Ordner landen und Sie alle Infos zuverlässig erhalten.
          </p>
        </div>
      </Card>
    )
  }

  return (
    <Card
      className="w-full max-w-[500px] h-auto min-h-[600px] p-6 shadow-lg flex flex-col sm:w-[90vw] sm:min-h-[500px]"
      style={{ backgroundColor: "#0f0f11", border: "1px solid #27272a" }}
    >
      {/* Header */}
      <div className="text-center mb-2">
        <h2 className="text-lg font-medium text-white mb-2">Individuelle Preisanfrage – einfach & unverbindlich</h2>
        <p className="text-[#787c87] text-xs">Schritt {currentStep} von 4</p>
        <div className="w-full bg-gray-700 rounded-full h-0.5 mt-2">
          <div
            className="bg-white h-0.5 rounded-full transition-all duration-300"
            style={{ width: `${(currentStep / 4) * 100}%` }}
          />
        </div>
      </div>

      {/* Form Content */}
      <div className="flex-1 min-h-0">
        <form onSubmit={handleSubmit} className="h-full flex flex-col">
          <div className="flex-1 min-h-0 mb-4">
            {/* Step 1: Company Info */}
            {currentStep === 1 && (
              <div className="space-y-4 h-full flex flex-col justify-start">
                <div>
                  <Label className="text-white text-sm font-medium">Unternehmensgröße</Label>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    {["1–10", "11–50", "51–200", "200+"].map((size) => (
                      <Button
                        key={size}
                        type="button"
                        variant={formData.companySize === size ? "default" : "outline"}
                        className={`h-10 text-sm transition-colors ${
                          formData.companySize === size ? "bg-white text-black" : "bg-transparent text-white"
                        }`}
                        style={{
                          border: formData.companySize === size ? "none" : "1px solid #27272a",
                          ...(formData.companySize === size && { pointerEvents: "none" }),
                        }}
                        onClick={() => setFormData((prev) => ({ ...prev, companySize: size }))}
                      >
                        {size} Mitarbeiter
                      </Button>
                    ))}
                  </div>
                </div>

                <div>
                  <Label className="text-white text-sm font-medium">Branche</Label>
                  <Select
                    value={formData.industry}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, industry: value }))}
                  >
                    <SelectTrigger
                      className="mt-2 text-white w-full"
                      style={{
                        backgroundColor: "#0f0f11",
                        border: "1px solid #27272a",
                        color: "#a1a1aa",
                      }}
                    >
                      <SelectValue placeholder="Branche auswählen" />
                    </SelectTrigger>
                    <SelectContent
                      className="max-h-48 w-full custom-scrollbar"
                      style={{
                        backgroundColor: "#0f0f11",
                        border: "1px solid #27272a",
                        width: "var(--radix-select-trigger-width)",
                      }}
                    >
                      {industries.map((industry) => (
                        <SelectItem
                          key={industry}
                          value={industry}
                          className="text-white hover:bg-[#1a1a1d] hover:text-white focus:bg-[#1a1a1d] focus:text-white w-full"
                        >
                          {industry}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-white text-sm font-medium">KI-Einsatzbereiche (Mehrfachauswahl)</Label>
                  <div className="grid grid-cols-2 gap-3 mt-2">
                    {aiAreas.map((area) => (
                      <div key={area} className="flex items-center space-x-2">
                        <Checkbox
                          id={area}
                          checked={formData.aiAreas.includes(area)}
                          onCheckedChange={() => handleCheckboxChange(area, "aiAreas")}
                          className="data-[state=checked]:bg-white data-[state=checked]:text-black"
                          style={{ border: "1px solid #27272a" }}
                        />
                        <Label htmlFor={area} className="text-white text-sm">
                          {area}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: Challenges & Goals */}
            {currentStep === 2 && (
              <div className="space-y-4 h-full flex flex-col justify-start">
                <div>
                  <Label className="text-white text-sm font-medium">Größte Herausforderung</Label>
                  <Select
                    value={formData.currentChallenge}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, currentChallenge: value }))}
                  >
                    <SelectTrigger
                      className="mt-2 w-full"
                      style={{
                        backgroundColor: "#0f0f11",
                        border: "1px solid #27272a",
                        color: "#a1a1aa",
                      }}
                    >
                      <SelectValue placeholder="Herausforderung wählen" />
                    </SelectTrigger>
                    <SelectContent 
                      className="custom-scrollbar"
                      style={{ 
                        backgroundColor: "#0f0f11", 
                        border: "1px solid #27272a",
                        width: "var(--radix-select-trigger-width)",
                      }}
                    >
                      {challenges.map((challenge) => (
                        <SelectItem
                          key={challenge}
                          value={challenge}
                          className="text-white hover:bg-[#1a1a1d] hover:text-white focus:bg-[#1a1a1d] focus:text-white w-full"
                        >
                          {challenge}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-white text-sm font-medium">Hauptziel</Label>
                  <Select
                    value={formData.mainGoal}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, mainGoal: value }))}
                  >
                    <SelectTrigger
                      className="mt-2 w-full"
                      style={{
                        backgroundColor: "#0f0f11",
                        border: "1px solid #27272a",
                        color: "#a1a1aa",
                      }}
                    >
                      <SelectValue placeholder="Ziel auswählen" />
                    </SelectTrigger>
                    <SelectContent 
                      className="custom-scrollbar"
                      style={{ 
                        backgroundColor: "#0f0f11", 
                        border: "1px solid #27272a",
                        width: "var(--radix-select-trigger-width)",
                      }}
                    >
                      {goals.map((goal) => (
                        <SelectItem
                          key={goal}
                          value={goal}
                          className="text-white hover:bg-[#1a1a1d] hover:text-white focus:bg-[#1a1a1d] focus:text-white w-full"
                        >
                          {goal}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label className="text-white text-sm font-medium">Spezielle Anfragen (optional)</Label>
                  <Textarea
                    placeholder="Beschreiben Sie besondere Anforderungen oder Wünsche..."
                    value={formData.specialMessage}
                    onChange={(e) => setFormData((prev) => ({ ...prev, specialMessage: e.target.value }))}
                    className="h-20 text-sm mt-2 placeholder:text-gray-400"
                    style={{
                      backgroundColor: "#0f0f11",
                      border: "1px solid #27272a",
                      color: "#a1a1aa",
                    }}
                  />
                </div>

                <div>
                  <Label className="text-white text-sm font-medium">Budget</Label>
                  <Select
                    value={formData.budget}
                    onValueChange={(value) => setFormData((prev) => ({ ...prev, budget: value }))}
                  >
                    <SelectTrigger
                      className="mt-2 w-full"
                      style={{
                        backgroundColor: "#0f0f11",
                        border: "1px solid #27272a",
                        color: "#a1a1aa",
                      }}
                    >
                      <SelectValue placeholder="Budget wählen" />
                    </SelectTrigger>
                    <SelectContent 
                      className="custom-scrollbar"
                      style={{ 
                        backgroundColor: "#0f0f11", 
                        border: "1px solid #27272a",
                        width: "var(--radix-select-trigger-width)",
                      }}
                    >
                      {budgetRanges.map((range) => (
                        <SelectItem
                          key={range}
                          value={range}
                          className="text-white hover:bg-[#1a1a1d] hover:text-white focus:bg-[#1a1a1d] focus:text-white w-full"
                        >
                          {range}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Step 3: Solutions */}
            {currentStep === 3 && (
              <div className="h-full flex flex-col">
                <Label className="text-white text-sm font-medium mb-3">Interessante Lösungen (Mehrfachauswahl)</Label>
                <div className="flex-1 grid grid-cols-2 gap-1.5 content-start">
                  {solutions.map((solution) => {
                    const isSelected = formData.interestedSolutions.includes(solution)
                    return (
                      <Button
                        key={solution}
                        type="button"
                        variant={isSelected ? "default" : "outline"}
                        className={`h-10 p-2 text-xs text-left whitespace-normal leading-tight transition-colors flex items-center ${
                          isSelected
                            ? "bg-white text-black"
                            : "bg-transparent text-white hover:bg-[#1a1a1d] hover:text-white"
                        }`}
                        style={{
                          border: isSelected ? "none" : "1px solid #27272a",
                        }}
                        onClick={() => handleCheckboxChange(solution, "interestedSolutions")}
                      >
                        {solution}
                      </Button>
                    )
                  })}
                </div>
              </div>
            )}

            {/* Step 4: Contact Info */}
            {currentStep === 4 && (
              <div className="space-y-4 h-full flex flex-col justify-start">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-white text-sm font-medium">Vorname</Label>
                    <Input
                      type="text"
                      placeholder="Max"
                      value={formData.firstName}
                      onChange={(e) => setFormData((prev) => ({ ...prev, firstName: e.target.value }))}
                      className="mt-2 placeholder:text-gray-400"
                      style={{
                        backgroundColor: "#0f0f11",
                        border: "1px solid #27272a",
                        color: "#a1a1aa",
                      }}
                      required
                    />
                  </div>
                  <div>
                    <Label className="text-white text-sm font-medium">Nachname</Label>
                    <Input
                      type="text"
                      placeholder="Mustermann"
                      value={formData.lastName}
                      onChange={(e) => setFormData((prev) => ({ ...prev, lastName: e.target.value }))}
                      className="mt-2 placeholder:text-gray-400"
                      style={{
                        backgroundColor: "#0f0f11",
                        border: "1px solid #27272a",
                        color: "#a1a1aa",
                      }}
                      required
                    />
                  </div>
                </div>

                <div>
                  <Label className="text-white text-sm font-medium">E-Mail für Kontaktaufnahme</Label>
                  <Input
                    type="email"
                    placeholder="max@unternehmen.de"
                    value={formData.email}
                    onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                    className="mt-2 placeholder:text-gray-400"
                    style={{
                      backgroundColor: "#0f0f11",
                      border: "1px solid #27272a",
                      color: "#a1a1aa",
                    }}
                    required
                  />
                </div>

                <div className="flex flex-col flex-grow">
                  <div className="p-4 rounded-lg mt-6" style={{ backgroundColor: "#1a1a1d" }}>
                    <p className="text-sm" style={{ color: "#a1a1aa" }}>
                      Nach dem Absenden erhalten Sie schnellstmöglich eine individuelle Kostenschätzung, abgestimmt auf Ihre Angaben.
                    </p>
                  </div>
                  <div className="mt-12 pt-4">
                    <div className="p-4 rounded-lg" style={{ backgroundColor: "transparent" }}>
                      <p className="text-xs text-center" style={{ color: "#a1a1aa" }}>
                        Ich bin damit einverstanden, dass Blcks meine personenbezogenen Daten im Rahmen der{" "}
                        <Link to="/agb" className="underline underline-offset-2 decoration-[#a1a1aa] hover:decoration-white transition-colors duration-200">
                          Nutzungsbedingungen
                        </Link>{" "}
                        und der{" "}
                        <Link to="/datenschutz" className="underline underline-offset-2 decoration-[#a1a1aa] hover:decoration-white transition-colors duration-200">
                          Datenschutzrichtlinie
                        </Link>{" "}
                        <br/>erhebt und verarbeitet.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Navigation */}
          <div
            className="flex justify-between items-center pt-4 flex-shrink-0 flex-wrap gap-2"
            style={{ borderTop: "1px solid #27272a" }}
          >
            {currentStep > 1 ? (
              <Button
                type="button"
                variant="outline"
                onClick={prevStep}
                className="bg-transparent text-white hover:bg-[#1a1a1d] hover:text-white transition-colors w-full sm:w-auto cursor-pointer"
                style={{ border: "1px solid #27272a" }}
              >
                <ChevronLeft className="w-4 h-4 mr-1" />
                Zurück
              </Button>
            ) : (
              <div></div>
            )}

            {currentStep < 4 ? (
              <Button
                type="button"
                onClick={nextStep}
                disabled={!isStepValid(currentStep)}
                className="bg-white text-black hover:bg-gray-200 disabled:opacity-50 transition-colors w-full sm:w-auto cursor-pointer"
              >
                Weiter
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            ) : (
              <Button
                type="submit"
                disabled={!isStepValid(4) || isSubmitting}
                className="bg-white text-black hover:bg-gray-200 disabled:opacity-50 transition-colors w-full sm:w-auto cursor-pointer"
              >
                {isSubmitting ? "Wird gesendet..." : "Anfrage senden"}
              </Button>
            )}
          </div>
        </form>
      </div>
    </Card>
  )
}
